/**
 * Hook customizado para sessão
 */

import { useState, useCallback, useEffect } from 'react';
import { SessionUtil } from '@utils';
import { VendedorSession, ClienteSession } from '@types';

interface UseSessionReturn {
  vendedor: VendedorSession | null;
  cliente: ClienteSession | null;
  isAuthenticated: boolean;
  canMakePrevisao: boolean;
  canMakePedido: boolean;
  operationType: 'previsao' | 'pedido' | null;
  setCliente: (codigo: string, nome: string) => void;
  clearCliente: () => void;
  logout: () => void;
}

export const useSession = (): UseSessionReturn => {
  const [vendedor, setVendedor] = useState<VendedorSession | null>(() =>
    SessionUtil.getVendedor()
  );
  const [cliente, setClienteState] = useState<ClienteSession | null>(() =>
    SessionUtil.getCliente()
  );

  // Atualiza estado quando sessão muda (para sincronização)
  useEffect(() => {
    const interval = setInterval(() => {
      const currentVendedor = SessionUtil.getVendedor();
      const currentCliente = SessionUtil.getCliente();

      if (JSON.stringify(currentVendedor) !== JSON.stringify(vendedor)) {
        setVendedor(currentVendedor);
      }

      if (JSON.stringify(currentCliente) !== JSON.stringify(cliente)) {
        setClienteState(currentCliente);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [vendedor, cliente]);

  const setCliente = useCallback((codigo: string, nome: string) => {
    SessionUtil.setCliente(codigo, nome);
    setClienteState({ cliCodigo: codigo, cliNome: nome });
  }, []);

  const clearCliente = useCallback(() => {
    SessionUtil.clearCliente();
    setClienteState(null);
  }, []);

  const logout = useCallback(() => {
    SessionUtil.logout();
    setVendedor(null);
    setClienteState(null);
  }, []);

  const isAuthenticated = !!vendedor;
  const canMakePrevisao = vendedor?.indPrevisao !== 0 && vendedor?.indPedido === 0;
  const canMakePedido = vendedor?.indPrevisao === 0 && vendedor?.indPedido === 1;
  const operationType = canMakePrevisao ? 'previsao' : canMakePedido ? 'pedido' : null;

  return {
    vendedor,
    cliente,
    isAuthenticated,
    canMakePrevisao,
    canMakePedido,
    operationType,
    setCliente,
    clearCliente,
    logout,
  };
};